/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, FileText, Compass, Eye, EyeOff, Download } from 'lucide-react';

/**
 * Phase 5.5 — Weekly Stewardship Letter™.
 *
 * Editorial review layout:
 *   • Quiet preview cards for each section
 *   • Subtle per-section mute toggles (posture summary is mandatory)
 *   • Single calm "Generate Stewardship Letter" action → PDF
 *
 * NEVER auto-publish. NEVER edit data — mute only suppresses sections.
 */
export default function StewardshipLetterPage() {
  const [centres, setCentres] = useState([]);
  const [centreId, setCentreId] = useState(null);
  const [letter, setLetter] = useState(null);
  const [muted, setMuted] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Pull centre list from territory map endpoint.
  useEffect(() => {
    axios.get(`${API}/terra-growth/territory-map?days=7`)
      .then(r => {
        const all = [...(r.data?.centres || []), ...(r.data?.unmapped_centres || [])];
        setCentres(all);
        if (!centreId && all.length > 0) setCentreId(all[0].centre_id);
      })
      .catch(() => setCentres([]));
  }, []);

  useEffect(() => {
    if (!centreId) return;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ centre_id: centreId, days: 7 });
    [...muted].forEach(m => params.append('muted', m));
    axios.get(`${API}/terra-growth/stewardship-letter/preview?${params.toString()}`)
      .then(r => setLetter(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not compose preview.'))
      .finally(() => setLoading(false));
  }, [centreId, muted]);

  const toggleMute = key => {
    setMuted(s => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const generatePdf = async () => {
    setGenerating(true); setError(null);
    try {
      const r = await axios.post(`${API}/terra-growth/stewardship-letter/generate`,
        { centre_id: centreId, muted: [...muted] },
        { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stewardship_letter_${centreId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not generate the PDF.');
    } finally { setGenerating(false); }
  };

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="stewardship-letter-page" className="terra-page-pad max-w-5xl mx-auto px-8 py-12">
        <Header />
        <CentrePicker centres={centres} centreId={centreId}
                      setCentreId={setCentreId} />

        {error && (
          <div data-testid="letter-error"
               className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 text-[13px] text-amber-900 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing the letter…
          </div>
        ) : letter ? (
          <>
            <Salutation letter={letter} />
            <ul className="space-y-4">
              {letter.sections.map(s => (
                <SectionCard key={s.key} section={s}
                             toggleMute={() => toggleMute(s.key)} />
              ))}
            </ul>
            <Actions onGenerate={generatePdf} generating={generating}
                      muted={muted} />
          </>
        ) : null}

        {letter && (
          <p className="text-[11px] text-[#8A8681] italic max-w-2xl mt-12 pt-6 border-t border-[#F4F2EE]">
            {letter.philosophy_note}
          </p>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════
function Header() {
  return (
    <header className="mb-10 max-w-2xl space-y-3">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
        <Compass className="w-3 h-3" /> Terra Growth OS™ · Stewardship
      </p>
      <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
        Weekly Stewardship Letter™
      </h1>
      <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
        A reflective ritual addressed to the Terra Head — composed once a week,
        not auto-published. Review each section, suppress what feels noisy,
        then quietly generate the PDF.
      </p>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Centre picker
// ═══════════════════════════════════════════════════════════════════
function CentrePicker({ centres, centreId, setCentreId }) {
  if (!centres?.length) return null;
  return (
    <div className="mb-8">
      <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] block mb-2">
        For centre
      </label>
      <select data-testid="centre-picker"
              value={centreId || ''}
              onChange={e => setCentreId(e.target.value)}
              className="appearance-none text-sm bg-white border border-[#D8D4CE] rounded-full px-4 py-2 text-[#2C2A29] focus:outline-none focus:border-[#4A5D4E] cursor-pointer min-w-[280px]">
        {centres.map(c => (
          <option key={c.centre_id} value={c.centre_id}>
            {c.centre_name}
          </option>
        ))}
      </select>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Salutation
// ═══════════════════════════════════════════════════════════════════
function Salutation({ letter }) {
  return (
    <article data-testid="letter-salutation"
             className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-5 mb-6">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] mb-2 flex items-center gap-1">
        <FileText className="w-3 h-3" /> Letter
      </p>
      <p className="text-base text-[#5C3D1F] font-['Outfit'] font-light">
        Addressed to the {letter.addressed_to}, for {letter.centre_name},
        week of <span className="italic">{letter.week_label}</span>.
      </p>
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section preview card
// ═══════════════════════════════════════════════════════════════════
function SectionCard({ section, toggleMute }) {
  return (
    <li data-testid={`section-${section.key}`}
        className={`rounded-2xl border bg-white p-5 transition ${
          section.muted ? 'border-dashed border-[#D8D4CE] bg-[#FBFaf9]' :
                          'border-[#EBE8E3]'}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681]">
          {section.title}
          {section.mandatory && (
            <span data-testid={`mandatory-${section.key}`}
                  className="ml-2 text-[9px] text-[#8C6239] tracking-[0.12em]">
              · always included
            </span>
          )}
        </p>
        {!section.mandatory && (
          <button data-testid={`mute-${section.key}`}
                  onClick={toggleMute}
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border transition ${
                    section.muted
                      ? 'bg-stone-50 text-stone-500 border-stone-200'
                      : 'bg-emerald-50/40 text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                  }`}>
            {section.muted ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {section.muted ? 'muted' : 'included'}
          </button>
        )}
      </header>
      <div className={`space-y-2 ${section.muted ? 'opacity-40' : ''}`}>
        {section.paragraphs.map((p, i) => (
          <p key={i} className="text-[13px] text-[#2C2A29] leading-relaxed font-['Work_Sans']">
            {p}
          </p>
        ))}
      </div>
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Generate action
// ═══════════════════════════════════════════════════════════════════
function Actions({ onGenerate, generating, muted }) {
  return (
    <footer className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-[#F4F2EE]">
      <button data-testid="generate-letter-btn"
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] bg-[#4A5D4E] text-white hover:bg-[#3A4D3E] disabled:opacity-40 transition">
        {generating ? <Loader2 className="w-4 h-4 animate-spin" />
                     : <Download className="w-4 h-4" />}
        Generate Stewardship Letter
      </button>
      <span data-testid="muted-count" className="text-[11px] text-[#8A8681]">
        {muted.size === 0 ? 'all sections included' :
         `${muted.size} section${muted.size !== 1 ? 's' : ''} muted`}
      </span>
    </footer>
  );
}
